import * as React from 'react';
import { Input as ShadcnInput } from '../ui/input';
import { cn } from '../../lib/utils';
import { FieldLabel } from './typography';

/* ────────────────────────────────────────────────────────────────────────────
   v9 form input wrappers. Shadcn Input under the hood — we just resize and
   restyle it to look like the .h-card form fields in v9 (rounded-2xl, slate-200
   border, py-2.5). Keeps the focus-visible ring + disabled state from shadcn.
   ──────────────────────────────────────────────────────────────────────────── */

export const HInput = React.forwardRef(function HInput({ className, ...rest }, ref) {
  return (
    <ShadcnInput
      ref={ref}
      className={cn(
        // Height-matches HPrimaryButton (38px min) so input+button rows line up.
        'min-h-[38px] h-auto py-2 px-3 text-[14px] rounded-lg',
        'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400',
        'focus-visible:ring-slate-900 focus-visible:ring-offset-1',
        'disabled:bg-slate-50 disabled:text-slate-500',
        className,
      )}
      {...rest}
    />
  );
});

/* HField — convenience wrapper of FieldLabel + HInput. */
export const HField = React.forwardRef(function HField(
  { label, htmlFor, helpText, error, className, inputClassName, ...inputProps },
  ref,
) {
  const id = htmlFor || inputProps.id;
  return (
    <div className={className}>
      {label && (
        <FieldLabel htmlFor={id} className="mb-1.5">
          {label}
        </FieldLabel>
      )}
      <HInput ref={ref} id={id} className={inputClassName} {...inputProps} />
      {error ? (
        <p className="mt-1 text-[11px] text-red-600">{error}</p>
      ) : helpText ? (
        <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{helpText}</p>
      ) : null}
    </div>
  );
});

/* OTP-style single-digit cell — used by VerifyCode. Styled like a small h-card.
   Active state (currently focused / first empty) gets a thicker dark border. */
export const HOtpCell = React.forwardRef(function HOtpCell(
  { active = false, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        'w-10 h-12 text-center text-[18px] font-semibold rounded-lg bg-white text-slate-900',
        'outline-none transition-colors',
        active
          ? 'border-2 border-slate-900'
          : 'border border-slate-200 focus-visible:border-slate-900 focus-visible:border-2',
        className,
      )}
      {...rest}
    />
  );
});
